/**
 * Janko Piano - Audio Module
 * Handles SpessaSynth initialization, note playback, and sustain
 * @version 0.2.63
 */

import { CONFIG, CHORD_INTERVALS, DB_CONFIG } from './config.js';

// =============================================================================
// AUDIO STATE
// =============================================================================

let AudioContextFunc = window.AudioContext || window.webkitAudioContext;
let audioContext = null;
let synth = null;
let currentInstId = 'piano';

// Audio effect nodes
let distortionNode = null;
let delayNode = null;
let delayFeedback = null;
let delayWet = null;
let effectChain = null;

// Sustain state
let isSustain = false;
let sustainedNotes = [];

// Polyphony tracking
let activeNoteCount = 0;

// Active pointers (shared with input module)
export let activePointers = new Map();

// =============================================================================
// INDEXEDDB HELPERS
// =============================================================================

function openDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_CONFIG.name, DB_CONFIG.version);
        request.onupgradeneeded = e => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(DB_CONFIG.storeName)) {
                db.createObjectStore(DB_CONFIG.storeName);
            }
        };
        request.onsuccess = e => resolve(e.target.result);
        request.onerror = e => reject(e);
    });
}

// =============================================================================
// AUDIO INITIALIZATION
// =============================================================================

export async function initAudio() {
    const overlay = document.getElementById('loading-overlay');
    const progress = document.getElementById('loading-progress');
    const loadText = document.getElementById('loading-text');

    if (overlay) overlay.classList.remove('hidden-overlay');

    // Initialize AudioContext
    if (!audioContext) {
        audioContext = new AudioContextFunc();
    }

    try {
        // NATIVE FLUIDSYNTH CHECK
        if (window.Capacitor && window.Capacitor.isNativePlatform()) {
            loadText.textContent = "Loading Native Engine...";
            try {
                await Capacitor.Plugins.FluidSynthPlugin.loadSoundFont({
                    path: 'public/assets/sounds/Jnsgm2.sf2'
                });
                console.log("Native FluidSynth Loaded");
                loadText.textContent = "Ready! (Native)";
                loadText.style.color = "#4CAF50";
                if (progress) progress.style.background = "#4CAF50";
                setTimeout(() => { overlay.classList.add('hidden-overlay'); }, 500);
                return;
            } catch (nativeErr) {
                console.error("Native FluidSynth Init Failed:", nativeErr);
                loadText.textContent = "Native Init Failed. Falling back...";
            }
        }

        let arrayBuffer;

        // 1. Try Loading from Cache (IndexedDB)
        try {
            loadText.textContent = "Checking Cache...";
            const db = await openDB();
            const tx = db.transaction(DB_CONFIG.storeName, 'readonly');
            const store = tx.objectStore(DB_CONFIG.storeName);
            const cached = await new Promise((res, rej) => {
                const req = store.get('TimGM6mb_sf2');
                req.onsuccess = () => res(req.result);
                req.onerror = () => rej(req.error);
            });

            if (cached) {
                console.log("Loaded from Cache!");
                arrayBuffer = cached;
                loadText.textContent = "Loaded from Cache (Instant)!";
                if (progress) progress.style.width = "100%";
            }
        } catch (e) {
            console.warn("Cache check failed:", e);
        }

        // 2. Fetch from Network if not cached
        if (!arrayBuffer) {
            loadText.textContent = "Requesting SoundFont...";

            let response;
            try {
                console.log("Fetching local: assets/sounds/Jnsgm2.sf2");
                response = await fetch('assets/sounds/Jnsgm2.sf2');
                if (!response.ok) throw new Error("Local fetch failed");
            } catch (err) {
                console.warn("Local fetch failed, trying online fallback...", err);
                loadText.textContent = "Local access failed. Trying Online Mirror...";
                response = await fetch('https://github.com/wrightflyer/SF2_SoundFonts/raw/master/Jnsgm2.sf2');
                if (!response.ok) throw new Error(`Online Fallback failed: ${response.status}`);
            }

            loadText.textContent = "Response ok. Reading stream...";

            const contentLength = response.headers.get('content-length');
            const total = parseInt(contentLength, 10) || 5969408;
            let loaded = 0;

            const reader = response.body.getReader();
            const chunks = [];

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                loaded += value.length;
                const pct = Math.min(100, Math.round((loaded / total) * 100));

                if (progress) progress.style.width = pct + "%";
                loadText.textContent = `Downloading... ${pct}%`;
            }

            // Concatenate chunks
            const arr = new Uint8Array(loaded);
            let pos = 0;
            for (let chunk of chunks) {
                arr.set(chunk, pos);
                pos += chunk.length;
            }
            arrayBuffer = arr.buffer;

            // Save to Cache
            try {
                const db = await openDB();
                const tx = db.transaction(DB_CONFIG.storeName, 'readwrite');
                const store = tx.objectStore(DB_CONFIG.storeName);
                store.put(arrayBuffer, 'TimGM6mb_sf2');
                console.log("Saved to Cache");
            } catch (e) { console.warn("Cache save failed:", e); }
        }

        // 3. Initialize SpessaSynth
        loadText.textContent = "Waiting for SpessaSynth Library...";

        let attempts = 0;
        while (!window.SpessaSynth && attempts < 20) {
            await new Promise(r => setTimeout(r, 500));
            attempts++;
            loadText.textContent = `Waiting for Library... (${attempts}/20)`;
        }

        if (!window.SpessaSynth) throw new Error("SpessaSynth Library failed to load. CDN blocked or offline?");

        const SynthClass = window.SpessaSynth.Synthesizer || window.SpessaSynth.WorkletSynthesizer || window.SpessaSynth.default;

        if (!SynthClass) {
            console.error("SpessaSynth Exports:", Object.keys(window.SpessaSynth));
            throw new Error("SpessaSynth class not found in exports.");
        }

        console.log("Using Synth Class:", SynthClass.name);

        try {
            if (SynthClass.name === 'WorkletSynthesizer') {
                console.log("Instantiating WorkletSynthesizer (Context, Config)...");

                try {
                    console.log("Adding AudioWorklet Module...");
                    await audioContext.audioWorklet.addModule('./js/spessasynth_processor.min.js');
                } catch (err) {
                    console.error("Failed to addModule:", err);
                    throw new Error("Could not load AudioWorklet module. Are you on a Server?");
                }

                synth = new SynthClass(audioContext, {});

                // Initialize audio effects
                initializeEffects();

                // Connect synth through effects chain to destination
                // Route: synth -> [dry path + effects paths] -> effectChain -> destination
                
                const synthOutput = synth.connect ? synth : synth.workletNode;
                
                // Dry path (bypasses all effects)
                synthOutput.connect(effectChain.dryGain);
                effectChain.dryGain.connect(effectChain);
                
                // Distortion path
                synthOutput.connect(distortionNode);
                distortionNode.connect(effectChain.distortionWet);
                effectChain.distortionWet.connect(effectChain);
                
                // Delay/Echo path (can be combined with distortion)
                synthOutput.connect(delayNode);
                delayWet.connect(effectChain);
                
                effectChain.connect(audioContext.destination);

                console.log("Loading SoundFont...");

                if (typeof synth.addSoundBank === 'function') {
                    console.log("Calling synth.addSoundBank");
                    await synth.addSoundBank(arrayBuffer);
                } else if (synth.soundBankManager && typeof synth.soundBankManager.addSoundBank === 'function') {
                    console.log("Calling synth.soundBankManager.addSoundBank");
                    await synth.soundBankManager.addSoundBank(arrayBuffer);
                } else {
                    console.error("No addSoundBank method found on synth:", synth);
                    throw new Error("Synth API mismatch: addSoundBank missing");
                }
            } else {
                console.log("Instantiating Standard Synthesizer (Destination, Buffer)...");
                synth = new SynthClass(audioContext.destination, arrayBuffer);
            }
        } catch (e) {
            console.error("Instantiation failed:", e);

            if (e.message.includes("addModule") || e.message.includes("AudioWorkletNode")) {
                loadText.innerHTML = "Error: AudioWorklet Failed.<br>Ensure you are using <b>Live Server</b> (http), not file://.<br>Check console for details.";
                throw e;
            }
            throw e;
        }

        // 4. Ready
        loadText.textContent = "Ready!";
        loadText.style.color = "#4CAF50";
        if (progress) progress.style.background = "#4CAF50";

        if (audioContext.state === 'suspended') {
            audioContext.resume().catch(() => console.log("Autoplay waiting for gesture..."));
        }

        setTimeout(() => {
            overlay.classList.add('hidden-overlay');
        }, 500);

        console.log("SpessaSynth Initialized");
        setInstrument(currentInstId);
        
        // Update HUD icon to match loaded instrument
        import('./ui.js').then(({ updateHudIcon }) => {
            updateHudIcon(currentInstId);
        });

    } catch (e) {
        console.error("Audio Load Error", e);
        let errorMsg = `Error: ${e.message}`;
        let tips = "";

        if (window.location.protocol === 'file:') {
            tips = "<br><br><b>CAUSE:</b> You are running this directly from a file (file://).<br>Browsers block this for security.<br><b>FIX:</b> Deploy to a web server (Netlify/Vercel) OR use a local server (VS Code Live Server).";
        } else if (e.message.includes("404")) {
            tips = "<br><br><b>CAUSE:</b> SoundFont file not found (404).<br><b>FIX:</b> meaningful assets folder check.";
        } else if (e.message.includes("Failed to fetch")) {
            tips = "<br><br><b>CAUSE:</b> Network Error or Missing File.<br>Check if 'assets/sounds/Jnsgm2.sf2' exists.";
        }

        loadText.innerHTML = `${errorMsg}${tips}<br><span style="font-size:10px; color:#888;">(See Console F12)</span>`;
        loadText.style.color = "#FF5555";
        if (progress) progress.style.background = "#FF5555";
    }
}

// =============================================================================
// AUDIO CONTEXT HELPERS
// =============================================================================

export function unlockAudio() {
    if (audioContext && audioContext.state === 'suspended') {
        audioContext.resume();
    }
}

export function getAudioContext() {
    return audioContext;
}

export function getSynth() {
    return synth;
}

// =============================================================================
// AUDIO EFFECTS
// =============================================================================

function initializeEffects() {
    if (!audioContext) return;
    
    // Create dry/wet gain nodes for proper mixing
    const dryGain = audioContext.createGain();
    dryGain.gain.value = 1.0; // Full dry signal by default
    
    // Create distortion node
    distortionNode = audioContext.createWaveShaper();
    distortionNode.curve = makeDistortionCurve(0);
    distortionNode.oversample = '4x';
    
    const distortionWet = audioContext.createGain();
    distortionWet.gain.value = 0; // No distortion by default
    
    // Create delay/echo effect
    delayNode = audioContext.createDelay(1.0);
    delayNode.delayTime.value = 0.3; // 300ms delay
    
    delayFeedback = audioContext.createGain();
    delayFeedback.gain.value = 0;
    
    delayWet = audioContext.createGain();
    delayWet.gain.value = 0;
    
    // Connect delay feedback loop
    delayNode.connect(delayFeedback);
    delayFeedback.connect(delayNode);
    delayNode.connect(delayWet);
    
    // Create effect chain container
    effectChain = audioContext.createGain();
    effectChain.gain.value = 1.0;
    
    // Store references for later use
    effectChain.dryGain = dryGain;
    effectChain.distortionWet = distortionWet;
}

function makeDistortionCurve(amount) {
    const samples = 44100;
    const curve = new Float32Array(samples);
    const deg = Math.PI / 180;
    
    for (let i = 0; i < samples; i++) {
        const x = (i * 2) / samples - 1;
        if (amount === 0) {
            curve[i] = x;
        } else {
            curve[i] = ((3 + amount) * x * 20 * deg) / (Math.PI + amount * Math.abs(x));
        }
    }
    return curve;
}

export function setDistortionAmount(amount) {
    if (!distortionNode || !effectChain) return;
    
    // Update distortion curve
    distortionNode.curve = makeDistortionCurve(amount * 100);
    
    // Control dry/wet mix
    if (effectChain.dryGain && effectChain.distortionWet) {
        // When distortion is 0, full dry signal. When distortion is 1, mix in wet signal
        effectChain.dryGain.gain.value = 1.0 - (amount * 0.5); // Reduce dry as distortion increases
        effectChain.distortionWet.gain.value = amount; // Increase wet as distortion increases
    }
}

export function setEchoAmount(amount) {
    if (!delayFeedback || !delayWet) return;
    // Set feedback (how much the delay repeats)
    delayFeedback.gain.value = amount * 0.6; // Max 60% feedback to avoid runaway
    // Set wet mix (how much echo is heard)
    delayWet.gain.value = amount;
}

// =============================================================================
// NOTE PLAYBACK
// =============================================================================

// Voice stealing: release oldest non-sustained note when polyphony limit reached
function stealOldestVoice() {
    let oldestPointer = null;
    let oldestTime = Infinity;
    
    // Find oldest non-sustained note
    activePointers.forEach((noteData, pointerId) => {
        // Skip if this pointer has sustained notes
        const hasSustainedNote = sustainedNotes.some(n => 
            noteData.some(d => d.midi === n.midi && d.channel === n.channel)
        );
        
        if (!hasSustainedNote && noteData.length > 0) {
            const firstNote = noteData[0];
            if (firstNote.startTime && firstNote.startTime < oldestTime) {
                oldestTime = firstNote.startTime;
                oldestPointer = pointerId;
            }
        }
    });
    
    // Release the oldest note
    if (oldestPointer !== null) {
        const noteData = activePointers.get(oldestPointer);
        if (noteData) {
            stopNote(noteData);
            activePointers.delete(oldestPointer);
            console.log(`[Voice Stealing] Released oldest note (polyphony: ${activeNoteCount}/${CONFIG.maxPolyphony})`);
        }
    }
}

export function playNote(midi, velocity = 127, channel = null) {
    if (!synth) return;
    unlockAudio();
    
    // Polyphony limit check with voice stealing
    if (activeNoteCount >= CONFIG.maxPolyphony) {
        stealOldestVoice();
    }

    // If channel not specified, use current instrument's channel
    if (channel === null) {
        const isDrums = (currentInstId === 'drums');
        channel = isDrums ? 9 : 0;
    }

    // NATIVE OVERRIDE
    if (window.Capacitor && window.Capacitor.isNativePlatform() && window.Capacitor.Plugins.FluidSynthPlugin) {
        window.Capacitor.Plugins.FluidSynthPlugin.noteOn({
            channel: channel,
            key: midi,
            velocity: velocity
        });
        activeNoteCount++;
        
        // MIDI Output
        if (window.sendMIDINoteOn) {
            window.sendMIDINoteOn(midi, velocity, channel);
        }
        
        // Recording
        if (window.recordMIDIEvent) {
            window.recordMIDIEvent('noteOn', midi, velocity, channel);
        }
        
        return { midi: midi, channel: channel, isNative: true };
    }

    synth.noteOn(channel, midi, velocity);
    activeNoteCount++;
    
    // MIDI Output
    if (window.sendMIDINoteOn) {
        window.sendMIDINoteOn(midi, velocity, channel);
    }
    
    // Recording
    if (window.recordMIDIEvent) {
        window.recordMIDIEvent('noteOn', midi, velocity, channel);
    }
    
    return { midi: midi, channel: channel };
}

export function stopNote(d) {
    if (!d || !synth) return;
    const list = Array.isArray(d) ? d : [d];
    list.forEach(item => {
        if (item && item.midi !== undefined) {
            if (isSustain) {
                sustainedNotes.push({ midi: item.midi, channel: item.channel, isNative: item.isNative });
            } else {
                if (item.isNative && window.Capacitor && window.Capacitor.Plugins.FluidSynthPlugin) {
                    window.Capacitor.Plugins.FluidSynthPlugin.noteOff({
                        channel: item.channel,
                        key: item.midi
                    });
                } else if (synth) {
                    synth.noteOff(item.channel, item.midi);
                }
                
                // Recording
                if (window.recordMIDIEvent) {
                    window.recordMIDIEvent('noteOff', item.midi, 0, item.channel);
                }
                // MIDI Output
                if (window.sendMIDINoteOff) {
                    window.sendMIDINoteOff(item.midi, item.channel);
                }
                
                // Decrement polyphony counter
                if (activeNoteCount > 0) activeNoteCount--;
            }
        }
    });
}

export function triggerNotes(midi, ptrId, keys) {
    let minMidi = Infinity, minPtrId = null;
    for (const [pId, data] of activePointers.entries()) {
        if (pId === ptrId) continue;
        const r = data.find(d => d.isRoot);
        if (r && r.midi < minMidi) { minMidi = r.midi; minPtrId = pId; }
    }
    
    let mode = CONFIG.chordMode;
    if (mode !== 'off') {
        if (midi > minMidi) mode = 'off';
        else if (minPtrId !== null) stripHarmony(minPtrId);
    }

    const ivals = CHORD_INTERVALS[mode] || [0];
    const activeData = [];

    const startTime = Date.now(); // Timestamp for voice stealing
    
    ivals.forEach(i => {
        const m = midi + i;
        const env = playNote(m);
        const ch = env ? env.channel : 0;

        const matching = keys.filter(k => k.midi === m);
        if (matching.length > 0) {
            matching.forEach(k => {
                activeData.push({ keyId: k.id, envelope: env, isRoot: (m === midi), midi: m, channel: ch, startTime: startTime });
            });
        } else {
            activeData.push({ keyId: 'hidden', envelope: env, isRoot: (m === midi), midi: m, channel: ch, startTime: startTime });
        }
    });
    return activeData;
}

function stripHarmony(pid) {
    const l = activePointers.get(pid);
    if (!l) return;
    const nl = [];
    l.forEach(i => {
        if (!i.isRoot) {
            if (synth) synth.noteOff(i.channel, i.midi);
        } else {
            nl.push(i);
        }
    });
    activePointers.set(pid, nl);
}

// =============================================================================
// SUSTAIN
// =============================================================================

export function releaseSustain() {
    isSustain = false;
    const count = sustainedNotes.length;
    sustainedNotes.forEach(n => {
        if (n.isNative && window.Capacitor && window.Capacitor.Plugins.FluidSynthPlugin) {
            window.Capacitor.Plugins.FluidSynthPlugin.noteOff({
                channel: n.channel,
                key: n.midi
            });
        } else if (synth) {
            synth.noteOff(n.channel, n.midi);
        }
    });
    // Decrement polyphony counter for all released sustained notes
    activeNoteCount = Math.max(0, activeNoteCount - count);
    sustainedNotes = [];
    document.body.classList.remove('sustain-active');
    const btn = document.getElementById('btn-sustain-toggle');
    if (btn) btn.classList.remove('active');
}

export function engageSustain() {
    isSustain = true;
    document.body.classList.add('sustain-active');
    const btn = document.getElementById('btn-sustain-toggle');
    if (btn) btn.classList.add('active');
}

export function toggleSustainLock() {
    if (isSustain) releaseSustain();
    else engageSustain();
}

export function isSustainActive() {
    return isSustain;
}

// =============================================================================
// INSTRUMENT MANAGEMENT
// =============================================================================

const GM_MAP = {
    piano: 0,
    bright_piano: 1,
    bass: 32,
    strings: 48,
    elec_guitar: 27,
    rhodes: 4,
    guitar: 24,
    organ: 16,
    synth: 80,
    drums: 0,
    trumpet: 56,
    violin: 40,
    flute: 73,
    synth_pad: 88,
    cello: 42,
    dist_guitar: 30,
    saxophone: 65,
    vibes: 11,
    marimba: 12,
    harmonica: 22
};

export function setInstrument(id) {
    currentInstId = id;
    
    if (id !== 'drums' && synth) {
        const prog = GM_MAP[id] !== undefined ? GM_MAP[id] : 0;
        synth.programChange(0, prog);
    }
    
    // Update MIDI playback channel filter based on instrument
    // When user switches instrument, filter visualization to that channel
    const isDrums = (id === 'drums');
    CONFIG.currentPlaybackChannel = isDrums ? 9 : 0;
    CONFIG.visualizeAllChannels = false; // Filter to current instrument only
    
    console.log(`[Instrument] Set to ${id}, visualizing channel ${CONFIG.currentPlaybackChannel}`);
    
    // Save to localStorage
    import('./settings.js').then(({ saveInstrument }) => saveInstrument(id));
}

export function getCurrentInstrument() {
    return currentInstId;
}

export function setCurrentInstrument(id) {
    currentInstId = id;
}
